import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardActions,
  Popover
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import useWindowSize from '../../../customHooks/useWindowSize';
import FlowCard from './FlowCard';
import DotStepper from './DotStepper';
import CustomForm from '../../Form';
import GenerateSchemaForFlow from './customFlowTemplateSchema/GenerateSchemaForFlow';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import CustomFlow from './CustomFlowComponents';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: '100%',
    marginTop: 40,
    marginBottom: 20,
    [theme.breakpoints.down('xs')]: { paddingLeft: 16, paddingRight: 16 },
    [theme.breakpoints.up('xs')]: { paddingLeft: 23, paddingRight: 23 },
    [theme.breakpoints.up('md')]: { paddingLeft: 23, paddingRight: 0 },
    [theme.breakpoints.up('lg')]: { paddingLeft: 41.5, paddingRight: 18.5 },
    [theme.breakpoints.up('xl')]: { paddingLeft: 80, paddingRight: 80 }
  },
  marginRight: {
    marginRight: 32
  },
  typography: {
    marginBottom: 20
  },
  arrowBtn: {
    width: 44,
    height: 44
  }
}));

const ScreenWithGrids = (props) => {
  const { config, onSubmit, loading, setTemplateId, document } = props;

  const flowTemplates = config.flows.filter(
    (template) => template.type === 'document' && template.visible
  );
  if (config.allowCustomFlow) {
    const customFlow = {
      name: 'Custom Flow',
      appId: config.name,
      description: 'Custom Flow',
      schema: '',
      steps: [],
      templateId: 'CustomFlow',
      templateName: 'Custom Flow',
      type: 'document',
      uischema: '',
      useForm: null,
      visible: true
    };
    flowTemplates.unshift(customFlow);
  }

  const windowSize = useWindowSize();
  const classes = useStyles();
  const submitFormButton = useRef(null);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({});

  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  const [cardsPerPage, setCardsPerPage] = useState(5);
  const [templatesToShow, setTemplatesToShow] = useState(
    flowTemplates.slice(0, 5).map((flowTemplate) => flowTemplate)
  );
  const [lastFlowTemplateIndex, setLastFlowTemplateIndex] = useState(5);
  const [customFlowStatus, setCustomFlowStatus] = useState(false);

  const [numberOfPages, setNumberOfPages] = useState(
    ~~(flowTemplates.length / cardsPerPage) +
      ((flowTemplates.length / cardsPerPage) % 1 > 0 ? 1 : 0)
  );
  const { t } = useTranslation();

  useEffect(() => {
    function evaluateCardsPerPage () {
      if (windowSize.width < 1440 && windowSize.width >= 1024) {
        return 4;
      } else if (windowSize.width >= 1440) {
        return 5;
      } else if (windowSize.width < 1024 && windowSize.width >= 760) {
        return 3;
      } else {
        return 2;
      }
    }

    const newItemsPerPage = evaluateCardsPerPage();
    if (newItemsPerPage !== cardsPerPage) {
      const targetCard =
        selected !== null
          ? activeStep * cardsPerPage + selected
          : lastFlowTemplateIndex - cardsPerPage;

      const pageNumber = ~~(targetCard / newItemsPerPage);

      setCardsPerPage(newItemsPerPage);
      setActiveStep(pageNumber);
      setLastFlowTemplateIndex(newItemsPerPage * (pageNumber + 1));
      setTemplatesToShow(
        flowTemplates
          .slice(
            pageNumber * newItemsPerPage,
            newItemsPerPage * (pageNumber + 1)
          )
          .map((flowTemplate) => flowTemplate)
      );
      if (selected !== null) {
        setSelected(targetCard % newItemsPerPage);
      }
      setNumberOfPages(
        ~~(flowTemplates.length / newItemsPerPage) +
          ((flowTemplates.length / newItemsPerPage) % 1 > 0 ? 1 : 0)
      );
    }
    if (flowTemplates.length === 1) {
      setCustomFlowStatus(flowTemplates[0].templateName === 'Custom Flow');
      setTemplateId(flowTemplates[0].templateId);
      setSelected(0);
    }
    setFormData({});
  }, []);
  useEffect(() => {
    setFormData({});
  }, [selected]);

  const handleNextPage = () => {
    setSelected(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setTemplatesToShow(
      flowTemplates
        .slice(lastFlowTemplateIndex, lastFlowTemplateIndex + cardsPerPage)
        .map((flowTemplate) => flowTemplate)
    );
    setLastFlowTemplateIndex((s) => s + cardsPerPage);
  };

  const handleBackPage = () => {
    setSelected(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setTemplatesToShow(
      flowTemplates
        .slice(
          lastFlowTemplateIndex - 2 * cardsPerPage,
          lastFlowTemplateIndex - cardsPerPage
        )
        .map((flowTemplate) => flowTemplate)
    );
    setLastFlowTemplateIndex((s) => s - cardsPerPage);
  };

  function transformErrors (errors) {
    // remove duplicates from errors
    const errorsWithoutDuplicates = errors.filter((error, index) => {
      const isDuplicate =
        errors.findIndex(
          (err) =>
            err.property === error.property && err.message === error.message
        ) !== index;
      return !isDuplicate;
    });

    return errorsWithoutDuplicates.filter(
      (error) => !['oneOf', 'enum'].includes(error.name)
    );
  }
  const SubmitButtons = () => {
    return (
      <Grid container item justify='flex-start'>
        <Button
          size='medium'
          color='primary'
          onClick={() => setSelected(null)}
          disabled={loading}
          className={classes.marginRight}
        >
          {t('createFlowWithGrids:cancel')}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => submitFormButton.current.click()}
          disabled={loading}
        >
          {t('createFlowWithGrids:create')}
        </Button>
      </Grid>

    );
  };

  const handleOnChange = ({ formData }) => {
    setFormData(formData);
  };

  return (
    <Grid container className={classes.mainContainer}>
      {flowTemplates.length > 1 &&
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={11}>
          <Typography variant='subtitle1' className={classes.typography}>
            {t('createFlowWithGrids:selectSignType')}
          </Typography>
        </Grid>
      </Grid>
      }
      <Grid container>
        {error && (
          <Popover
            open
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'center'
            }}
          >
            <Card>
              <CardHeader title='Error' />
              <CardContent>
                <Typography>{error}</Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant='contained'
                  onClick={() => setError(null)}
                  color='primary'
                >
                  {t('createFlowWithGrids:close')}
                </Button>
              </CardActions>
            </Card>
          </Popover>
        )}

        {flowTemplates.length > 1 &&
        <Grid container direction='row' alignItems='center'>
          <Grid container item xs={1} direction='row' justify='flex-end'>
            <Button
              className={classes.arrowBtn}
              onClick={handleBackPage}
              disabled={activeStep === 0}
              style={{
                display: activeStep === 0 && 'none'
              }}
            >
              <KeyboardArrowLeft style={{ fontSize: 44 }} />
            </Button>
          </Grid>
          <Grid
            container
            item
            xs={10}
            spacing={3}
            direction='row'
            wrap='nowrap'
            justify='flex-start'
          >
            {templatesToShow.map((flow, index) => {
              return (
                <FlowCard
                  key={index}
                  flow={flow}
                  selected={selected === index}
                  onSelect={() => {
                    setCustomFlowStatus(flow.templateName === 'Custom Flow');
                    setTemplateId(flow.templateId);
                    setSelected(index);
                  }}
                />
              );
            })}
          </Grid>
          <Grid container item xs={1} direction='row' justify='flex-start'>
            <Button
              className={classes.arrowBtn}
              onClick={handleNextPage}
              disabled={activeStep === numberOfPages - 1}
              style={{
                display: activeStep === numberOfPages - 1 && 'none'
              }}
            >
              <KeyboardArrowRight style={{ fontSize: 44 }} />
            </Button>
          </Grid>
          <Grid item xs={12} style={{ marginTop: 24 }}>
            <DotStepper
              activeStep={activeStep}
              numOfSteps={numberOfPages === 0 ? 1 : numberOfPages}
            />
          </Grid>
        </Grid>
        }
      </Grid>
      {selected !== null && (
        <Grid
          container
          direction='column'
          jutify={loading ? 'center' : undefined}
          alignItems={loading ? 'center' : undefined}
        >
          <Grid container spacing={3} direction='row'>
            {!customFlowStatus
              ? <Grid item xs={5}>
                <CustomForm
                  formRef={formRef}
                  transformErrors={transformErrors}
                  formData={formData}
                  onChange={handleOnChange}
                  submitFormButton={submitFormButton}
                  schema={
                    !customFlowStatus
                      ? customizeSchema(
                        templatesToShow[selected].schema,
                        document
                      )
                      : customizeSchema(
                        GenerateSchemaForFlow(
                          config.steps.filter(
                            (elm) =>
                              elm.step.type !== 'notificationStep' &&
                              elm.step.type !== 'subFlows' &&
                              !elm.step.type.includes('biometric')
                          )
                        ),
                        document
                      )
                  }
                  uiSchema={
                    !customFlowStatus
                      ? customizeUiSchema(templatesToShow[selected].uischema)
                      : undefined
                  }
                  onSubmit={(form) => onSubmit(form.formData)}
                />
              </Grid> : (
                <CustomFlow SubmitButtons={SubmitButtons} setSelected={setSelected} onSubmit={onSubmit} formRef={formRef} submitFormButton={submitFormButton} steps={config.steps.filter(
                  (elm) =>
                    elm.step.type !== 'notificationStep' &&
                      elm.step.type !== 'subFlows' &&
                      !elm.step.type.includes('biometric') &&
                      elm.step.type !== 'qrSignStep' &&
                      elm.step.type !== 'transactSignStep'
                )} document={document} />
              ) }

          </Grid>
          {!customFlowStatus &&
          <Grid container direction='row'>
            <SubmitButtons />
          </Grid>
          }

        </Grid>
      )}
    </Grid>
  );
};

const customizeSchema = (jsonSchema, document) => {
  if (jsonSchema && jsonSchema.properties && jsonSchema.properties.byFile) {
    const byFileOriginalObject = JSON.parse(JSON.stringify(jsonSchema.properties.byFile.items));
    if (Array.isArray(byFileOriginalObject)) {
      // multiples renders
      return jsonSchema;
    }
    // principal
    const princiaplByFile = JSON.parse(JSON.stringify(byFileOriginalObject));
    princiaplByFile.title = `[1] ${document.principal.name}`;
    jsonSchema.properties.byFile.items = [princiaplByFile];
    document.annexes.forEach((annex, index) => {
      const byFile = JSON.parse(JSON.stringify(byFileOriginalObject));
      byFile.title = `[${index + 2}] ${annex.name}`;
      jsonSchema.properties.byFile.items.push(byFile);
    });
  }

  return jsonSchema;
};

const customizeUiSchema = (uischema) => {
  /*   const jsonUischema = JSON.parse(uischema); */
  if (uischema && uischema.byFile) {
    uischema.byFile = {
      'ui:options': {
        orderable: false,
        removable: false
      }
    };
  }
  return uischema;
};

ScreenWithGrids.propTypes = {
  config: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setTemplateId: PropTypes.func.isRequired
};

export default ScreenWithGrids;
